import { API_CONFIG } from "../config/api";

const API_BASE_URL = API_CONFIG.BASE_URL;

export class RiskService {
  static async getAuthHeaders(getAccessTokenSilently) {
    const token = await getAccessTokenSilently();
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  }

  static async createRisk(data, getAccessTokenSilently) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(`${API_BASE_URL}/risks`, {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to create risk");
    }

    return response.json();
  }

  static async associateOrganizationRisk(data, getAccessTokenSilently) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(`${API_BASE_URL}/risks/associate`, {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error || "Failed to associate organization risk"
      );
    }

    return response.json();
  }

  static async getOrganizationRisks(organizationId, getAccessTokenSilently) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(
      `${API_BASE_URL}/risks/organization/${organizationId}`,
      {
        headers,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to fetch organization risks");
    }

    return response.json();
  }

  static async getRisksForBusinessUnit(businessUnitId, getAccessTokenSilently) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(
      `${API_BASE_URL}/risks/business-unit/${businessUnitId}`,
      {
        headers,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to fetch risks");
    }

    return response.json();
  }

  static async getAvailableRisksForAssociation(
    businessUnitId,
    getAccessTokenSilently
  ) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(
      `${API_BASE_URL}/risks/available-for-association/${businessUnitId}`,
      {
        headers,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error || "Failed to fetch available risks for association"
      );
    }

    return response.json();
  }

  static async getMyRisks(getAccessTokenSilently) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(`${API_BASE_URL}/risks/my-risks`, {
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to fetch your risks");
    }

    return response.json();
  }

  static async getRiskDetails(riskId, getAccessTokenSilently) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(`${API_BASE_URL}/risks/${riskId}`, {
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to fetch risk details");
    }

    return response.json();
  }

  static async updateRisk(riskId, data, getAccessTokenSilently) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(`${API_BASE_URL}/risks/${riskId}`, {
      method: "PUT",
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to update risk");
    }

    return response.json();
  }

  static async deleteRisk(riskId, getAccessTokenSilently) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(`${API_BASE_URL}/risks/${riskId}`, {
      method: "DELETE",
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to delete risk");
    }

    return response.json();
  }

  // DEPRECATED: Use MetadataService.getRiskCreationCategories instead
  static async getRiskTypes(getAccessTokenSilently) {
    console.warn(
      "getRiskTypes is deprecated. Use MetadataService.getRiskCreationCategories instead."
    );
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(`${API_BASE_URL}/risks/metadata/categories`, {
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to fetch categories");
    }

    return response.json();
  }

  static async getSeverities(getAccessTokenSilently) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(`${API_BASE_URL}/risks/metadata/severities`, {
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to fetch severities");
    }

    return response.json();
  }

  static async getLikelihoods(getAccessTokenSilently) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(`${API_BASE_URL}/risks/metadata/likelihoods`, {
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to fetch likelihoods");
    }

    return response.json();
  }

  static async getAvailableControlsForRisk(riskId, getAccessTokenSilently) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(
      `${API_BASE_URL}/risks/${riskId}/available-controls`,
      {
        headers,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error || "Failed to fetch available controls for risk"
      );
    }

    return response.json();
  }

  static async associateControlWithRisk(
    riskId,
    controlId,
    getAccessTokenSilently
  ) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(
      `${API_BASE_URL}/risks/${riskId}/controls/${controlId}`,
      {
        method: "POST",
        headers,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error || "Failed to associate control with risk"
      );
    }

    return response.json();
  }

  static async removeControlFromRisk(
    riskId,
    controlId,
    getAccessTokenSilently
  ) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(
      `${API_BASE_URL}/risks/${riskId}/controls/${controlId}`,
      {
        method: "DELETE",
        headers,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to remove control from risk");
    }

    return response.json();
  }
}
